import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faCheckCircle,
  faTimesCircle,
  faArrowRight,
  faArrowLeft,
  faPlay,
  faTrophy,
  faClock,
  faRedo,
  faSave,
  faSpinner,
  faGraduationCap,
  faBookOpen,
  faChartBar,
  faHistory,
  faTrash,
  faInfoCircle,
  faChevronLeft,
  faLightbulb,
  faCalendarAlt,
  faBrain
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../Shared/Loader";
import Pagination from "../../Shared/Pagination";
import DeleteAlert from "../../Shared/DeleteAlert";
import { storageHelper } from "../../../Helpers/storageHelper";
import ApiServices from "../../Services/ApiServices";
import { toastHelper } from '../../../Helpers/toastHelper';
import Header from "../../Shared/Header";
import botImage from "../../../assets/images/bot.png";
import MiniLoader from "../../Shared/MiniLoader";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const StudentSelfExam = () => {
  // State Management
  const [step, setStep] = useState("start"); // start, chat, loading, exam, result
  const [userClass, setUserClass] = useState("");
  const [material, setMaterial] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [questionCount, setQuestionCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Exam Data
  const [examData, setExamData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { questionIndex: selectedOption }
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // Result Calculation
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);

  // Chat State
  const [chatMessages, setChatMessages] = useState([]);

  // History State
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Delete State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const cls = storageHelper.getItem("user_class_name") || "غير محدد";
    const mat = storageHelper.getItem("material") || "غير محدد";
    setUserClass(cls);
    setMaterial(mat);
  }, []);

  const getOptionLabels = () => {
    const latinSubjects = [
      'english', 'انجليزي', 'إنجليزي', 'الإنجليزية', 'اللغة الإنجليزية',
      'french', 'فرنساوي', 'فرنساوى', 'فرنسي', 'فرنسى', 'الفرنسية', 'اللغة الفرنسية',
      'german', 'الماني', 'ألماني', 'المانية', 'الألمانية', 'اللغة الالمانية',
      'spanish', 'اسباني', 'إسباني', 'اسبانية', 'الإسبانية', 'اللغة الاسبانية',
      'italian', 'ايطالي', 'إيطالي', 'ايطالية', 'الإيطالية', 'اللغة الايطالية'
    ];
    const materialLower = material.toLowerCase().trim();
    const isLatinSubject = latinSubjects.some(subject => materialLower.includes(subject));
    return isLatinSubject ? ["A", "B", "C", "D"] : ["أ", "ب", "ج", "د"];
  };

  const startChat = () => {
    setStep("chat");
    setChatMessages([
      {
        id: Date.now(),
        sender: "bot",
        text: `أهلاً بك يا بطل! أنا رفيق، رفيقك في رحلة التعلّم. 
        لقد لاحظت أنك تدرس ${material} في ${userClass}. 
        هل تود أن نختبر معلوماتك اليوم باختبار ذكي؟`,
        timestamp: new Date()
      }
    ]);
  };

  const generateExam = async () => {
    setLoading(true);
    setStep("loading");
    try {
      const encodedClass = encodeURIComponent(userClass);
      const encodedMaterial = encodeURIComponent(material);
      const url = `Exam/StudentSelf/Generate?user_class=${encodedClass}&material=${encodedMaterial}&question_count=${questionCount}&difficulty=${difficulty}`;
      const response = await ApiServices.get(url);
      if (response && response.questions) {
        setExamData(response);
        setStep("exam");
        setStartTime(new Date());
        setCurrentQuestionIndex(0);
        setUserAnswers({});
      } else {
        throw new Error("فشل في استلام بيانات الاختبار");
      }
    } catch (err) {

      setError("عذراً، لم أستطع إنشاء الاختبار حالياً. حاول مرة أخرى لاحقاً.");
      setStep("start");
      toastHelper.error("فشل في إنشاء الاختبار");
    } finally {
      setLoading(false);
    }
  };

  const finishExam = () => {
    const end = new Date();
    setEndTime(end);
    const totalQuestions = examData.questions.length;
    let correctAnswers = 0;
    examData.questions.forEach((q, idx) => {
      const userAnswer = userAnswers[idx];
      const options = q.options;
      const correctAnswerText = q.correctAnswer;
      const correctIndex = options.findIndex(opt => {
        if (correctAnswerText.length <= 2 && /^[A-D]$/i.test(correctAnswerText.trim())) {
          return opt.trim().toUpperCase().startsWith(correctAnswerText.trim().toUpperCase() + '.');
        }
        return opt === correctAnswerText || opt.trim() === correctAnswerText.trim();
      });
      if (userAnswer === correctIndex) correctAnswers++;
    });
    const scorePercentage = (correctAnswers / totalQuestions) * 100;
    const durationSeconds = Math.floor((end - startTime) / 1000);
    setResults({
      exam_title: examData.examTitle,
      total_questions: totalQuestions,
      correct_answers: correctAnswers,
      score_percentage: Math.round(scorePercentage),
      is_passed: scorePercentage >= 50,
      started_at: startTime.toISOString(),
      completed_at: end.toISOString(),
      duration_seconds: durationSeconds,
      student_id: parseInt(storageHelper.getItem("user_id"), 10)
    });
    setStep("result");
  };

  const fetchHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const url = `StudentSelfExam/student?PageSize=6&PageNumber=${page}`;
      const response = await ApiServices.get(url);
      if (response && response.check) {
        setHistoryData(response.data.items);
        setTotalRecords(response.data.total_records);
        setCurrentPage(page);
      }
    } catch (err) {
      toastHelper.error("فشل في تحميل سجل الاختبارات");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory) fetchHistory(1);
    setShowHistory(!showHistory);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(`StudentSelfExam/delete/${itemToDelete.id}`);
      if (response && response.check) {
        toastHelper.success("تم حذف الاختبار بنجاح");
        fetchHistory(currentPage);
        setIsDeleteModalOpen(false);
      }
    } catch (err) {
      toastHelper.error("حدث خطأ أثناء الحذف");
    } finally {
      setDeleteLoading(false);
    }
  };

  const saveResults = async () => {
    setSaving(true);
    try {
      const response = await ApiServices.post("StudentSelfExam/create", results);
      if (response && response.check) toastHelper.success("تم حفظ نتيجة الاختبار بنجاح!");
    } catch (err) {
      toastHelper.error("حدث خطأ في الاتصال بالخادم.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ss-page">
      <Header />
      <br />
      <section className="ss-root" id="self-exam">
        <div className="ss-container">
          {/* ── header ── */}
          <motion.div
            className="ss-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="ss-eyebrow" variants={fadeUp} custom={0}>
              <span className="ss-eyebrow-dot" />
              اختبار ذكي مخصص
            </motion.div>
            <motion.h2 className="ss-title" variants={fadeUp} custom={1}>
              مساعدك الذكي <em>رفيق</em>
            </motion.h2>
            <motion.p className="ss-subtitle" variants={fadeUp} custom={2}>
              استخدم قوة الذكاء الاصطناعي لإنشاء اختبارات مخصصة لمستواك الدراسي وموادك المفضلة
            </motion.p>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* STEP: START */}
            {step === "start" && (
              <motion.div
                key="start"
                className="ss-start-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="ss-hero-card">
                  <div className="ss-hero-bg" />
                  <div className="ss-hero-content">
                    <div className="ss-bot-avatar">
                      <img src={botImage} alt="Rafiq" />
                      <div className="ss-bot-glow" />
                    </div>
                    <div className="ss-hero-text">
                      <h3>أهلاً بك يا بطل!</h3>
                      <p>أنا رفيق، مساعدك الذكي. سأقوم بتصميم اختبار مخصص لك بناءً على منهجك الدراسي.</p>
                      <div className="ss-hero-info">
                        <div className="ss-info-tag">
                          <FontAwesomeIcon icon={faGraduationCap} />
                          <span>{userClass}</span>
                        </div>
                        <div className="ss-info-tag">
                          <FontAwesomeIcon icon={faBookOpen} />
                          <span>{material}</span>
                        </div>
                      </div>
                      <div className="ss-hero-actions">
                        <button className="ss-btn-primary" onClick={startChat}>
                          <FontAwesomeIcon icon={faPlay} />
                          ابدأ الاختبار الآن
                        </button>
                        <button className={`ss-btn-outline ${showHistory ? 'active' : ''}`} onClick={toggleHistory}>
                          <FontAwesomeIcon icon={faHistory} />
                          {showHistory ? "إخفاء السجل" : "سجل اختباراتي"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {showHistory && (
                  <motion.div
                    className="ss-history-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="ss-section-title">
                      <FontAwesomeIcon icon={faHistory} />
                      <h4>سجل الاختبارات السابقة</h4>
                    </div>
                    {historyLoading ? (
                      <div className="ss-history-loader"><MiniLoader size={15} color="#6366f1" /></div>
                    ) : (
                      <div className="ss-history-grid">
                        {historyData.map((item, idx) => (
                          <HistoryCard
                            key={item.id}
                            item={item}
                            idx={idx}
                            onDelete={(it) => { setItemToDelete(it); setIsDeleteModalOpen(true); }}
                          />
                        ))}
                      </div>
                    )}
                    {totalRecords > 6 && (
                      <div className="ss-pagination">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={Math.ceil(totalRecords / 6)}
                          onPageChange={fetchHistory}
                        />
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* STEP: CHAT */}
            {step === "chat" && (
              <motion.div
                key="chat"
                className="ss-chat-view"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="ss-chat-container">
                  <div className="ss-chat-header">
                    <img src={botImage} alt="Rafiq" />
                    <div className="ss-chat-status">
                      <h5>رفيق الذكي</h5>
                      <span><span className="dot" /> متصل الآن</span>
                    </div>
                  </div>
                  <div className="ss-chat-body">
                    {chatMessages.map(msg => (
                      <div key={msg.id} className={`ss-msg ${msg.sender}`}>
                        <div className="ss-msg-bubble">{msg.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="ss-chat-footer">
                    <div className="ss-options-box">
                      <div className="ss-option-group">
                        <label>مستوى الصعوبة</label>
                        <div className="ss-pills">
                          {['easy', 'medium', 'hard'].map(d => (
                            <button key={d} className={`ss-pill ${difficulty === d ? 'active' : ''}`} onClick={() => setDifficulty(d)}>
                              {d === 'easy' ? 'سهل' : d === 'medium' ? 'متوسط' : 'صعب'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="ss-option-group">
                        <label>عدد الأسئلة</label>
                        <div className="ss-pills">
                          {[5, 10, 15, 20].map(c => (
                            <button key={c} className={`ss-pill ${questionCount === c ? 'active' : ''}`} onClick={() => setQuestionCount(c)}>
                              {c}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button className="ss-generate-btn" onClick={generateExam}>
                      <span>أنشئ الاختبار لي!</span>
                      <FontAwesomeIcon icon={faRobot} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP: LOADING */}
            {step === "loading" && (
              <motion.div key="loading" className="ss-loading-view" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="ss-ai-loader">
                  <div className="ss-loader-bot">
                    <img src={botImage} alt="bot" />
                    <div className="ss-loader-ring" />
                  </div>
                  <h3>جاري تحضير الأسئلة...</h3>
                  <p>رفيق يقوم الآن بصياغة اختبار مثالي لمستواك</p>
                  <div className="ss-progress-track">
                    <motion.div className="ss-progress-fill" animate={{ width: ["0%", "100%"] }} transition={{ duration: 2, repeat: Infinity }} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP: EXAM */}
            {step === "exam" && examData && (
              <motion.div key="exam" className="ss-exam-view" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="ss-exam-header-sticky">
                  <div className="ss-header-top">
                    <div className="ss-exam-title-box">
                      <h4>{examData.examTitle}</h4>
                      <div className="ss-meta-pills">
                        <span className="ss-meta-pill"><FontAwesomeIcon icon={faBrain} /> {difficulty === 'easy' ? 'سهل' : difficulty === 'medium' ? 'متوسط' : 'صعب'}</span>
                        <span className="ss-meta-pill"><FontAwesomeIcon icon={faClock} /> {new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="ss-progress-summary">
                      <span>{currentQuestionIndex + 1} / {examData.questions.length}</span>
                      <div className="ss-mini-bar"><div className="fill" style={{ width: `${((currentQuestionIndex + 1) / examData.questions.length) * 100}%` }} /></div>
                    </div>
                  </div>
                </div>

                <div className="ss-question-card-wrap">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentQuestionIndex}
                      className="ss-question-card"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <div className="ss-question-num">سؤال {currentQuestionIndex + 1}</div>
                      <h3 className="ss-question-text">{examData.questions[currentQuestionIndex].questionText}</h3>
                      <div className="ss-options-grid">
                        {examData.questions[currentQuestionIndex].options.map((option, idx) => {
                          const isSelected = userAnswers[currentQuestionIndex] === idx;
                          return (
                            <button
                              key={idx}
                              className={`ss-option-btn ${isSelected ? 'selected' : ''}`}
                              onClick={() => setUserAnswers(p => ({ ...p, [currentQuestionIndex]: idx }))}
                            >
                              <span className="ss-opt-label">{getOptionLabels()[idx]}</span>
                              <span className="ss-opt-text">{option}</span>
                              {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="ss-check" />}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="ss-exam-footer">
                  <button className="ss-footer-btn prev" disabled={currentQuestionIndex === 0} onClick={() => setCurrentQuestionIndex(p => p - 1)}>
                    <FontAwesomeIcon icon={faArrowRight} />
                    السابق
                  </button>
                  <button className="ss-footer-btn next" disabled={userAnswers[currentQuestionIndex] === undefined}
                    onClick={() => currentQuestionIndex < examData.questions.length - 1 ? setCurrentQuestionIndex(p => p + 1) : finishExam()}>
                    {currentQuestionIndex < examData.questions.length - 1 ? "التالي" : "إنهاء الاختبار"}
                    <FontAwesomeIcon icon={currentQuestionIndex < examData.questions.length - 1 ? faArrowLeft : faCheckCircle} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP: RESULT */}
            {step === "result" && results && (
              <motion.div key="result" className="ss-result-view" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="ss-result-card">
                  <div className={`ss-result-header ${results.is_passed ? 'pass' : 'fail'}`}>
                    <div className="ss-trophy-icon">
                      <FontAwesomeIcon icon={results.is_passed ? faTrophy : faTimesCircle} />
                    </div>
                    <h2>{results.is_passed ? "مبروك النجاح!" : "محاولة جيدة!"}</h2>
                    <p>{results.is_passed ? "لقد أبدعت في هذا الاختبار، واصل التميز" : "لا تيأس، الممارسة تجعلك أفضل، جرب مرة أخرى"}</p>
                  </div>

                  <div className="ss-result-stats">
                    <div className="ss-res-stat">
                      <span className="val">{results.score_percentage}%</span>
                      <span className="lab">الدرجة النهائية</span>
                    </div>
                    <div className="ss-res-stat">
                      <span className="val">{results.correct_answers} / {results.total_questions}</span>
                      <span className="lab">الإجابات الصحيحة</span>
                    </div>
                    <div className="ss-res-stat">
                      <span className="val">{Math.floor(results.duration_seconds / 60)}د {results.duration_seconds % 60}ث</span>
                      <span className="lab">الوقت المستغرق</span>
                    </div>
                  </div>

                  <div className="ss-result-actions">
                    <button className="ss-btn-save" onClick={saveResults} disabled={saving}>
                      {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                      حفظ النتيجة
                    </button>
                    <button className="ss-btn-retry" onClick={() => setStep("start")}>
                      <FontAwesomeIcon icon={faRedo} />
                      اختبار جديد
                    </button>
                  </div>

                  <div className="ss-review-section">
                    <h5>تقرير الإجابات</h5>
                    <div className="ss-review-list">
                      {examData.questions.map((q, idx) => {
                        const userAnswerIdx = userAnswers[idx];
                        const correctAnswerText = q.correctAnswer;
                        const correctIdx = q.options.findIndex(opt => {
                          if (correctAnswerText.length <= 2 && /^[A-D]$/i.test(correctAnswerText.trim())) {
                            return opt.trim().toUpperCase().startsWith(correctAnswerText.trim().toUpperCase() + '.');
                          }
                          return opt === correctAnswerText || opt.trim() === correctAnswerText.trim();
                        });
                        const isCorrect = userAnswerIdx === correctIdx;
                        return (
                          <div key={idx} className={`ss-review-item ${isCorrect ? 'correct' : 'wrong'}`}>
                            <div className="ss-rev-q">{idx + 1}. {q.questionText}</div>
                            <div className="ss-rev-options">
                              {q.options.map((opt, oidx) => (
                                <div key={oidx} className={`ss-rev-opt ${oidx === correctIdx ? 'right' : ''} ${oidx === userAnswerIdx && !isCorrect ? 'user-wrong' : ''}`}>
                                  {opt}
                                  {oidx === correctIdx && <FontAwesomeIcon icon={faCheckCircle} />}
                                  {oidx === userAnswerIdx && !isCorrect && <FontAwesomeIcon icon={faTimesCircle} />}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <DeleteAlert
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title="حذف سجل الاختبار"
        message="هل أنت متأكد من حذف هذا الاختبار من سجلك؟"
        itemName={itemToDelete?.exam_title}
      />

      <style>{`
        .ss-page { background: #fff; min-height: 100vh; }
        .ss-root {
          font-family: "Zain", sans-serif; direction: rtl;
          padding: 80px 0 100px; isolation: isolate;
        }
        .ss-container { max-width: 1260px; margin: 0 auto; padding: 0 40px; }

        /* Header */
        .ss-header { text-align: center; margin-bottom: 50px; }
        .ss-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .ss-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: ss-pulse 2.2s ease-in-out infinite;
        }
        @keyframes ss-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .ss-title { font-size: clamp(2.2rem, 4vw, 3.6rem); font-weight: 900; color: #0f172a; margin: 0 0 18px; line-height: 1.2; }
        .ss-title em { font-style: normal; background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .ss-subtitle { font-size: 1.08rem; color: #64748b; line-height: 1.85; max-width: 700px; margin: 0 auto; }

        /* Hero Card */
        .ss-hero-card {
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 40px; padding: 60px;
          position: relative; overflow: hidden; color: #0f172a; margin-bottom: 60px;
        }
        .ss-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(circle at 10% 20%, rgba(99,102,241,0.05) 0%, transparent 50%),
                      radial-gradient(circle at 90% 80%, rgba(232,146,10,0.05) 0%, transparent 50%);
        }
        .ss-hero-content { position: relative; z-index: 2; display: flex; align-items: center; gap: 60px; }
        .ss-bot-avatar { position: relative; flex-shrink: 0; }
        .ss-bot-avatar img { width: 180px; height: 180px; animation: ss-float 4s ease-in-out infinite; }
        @keyframes ss-float { 0%,100%{ transform: translateY(0); } 50%{ transform: translateY(-20px); } }
        .ss-bot-glow { position: absolute; inset: 20px; background: #6366f1; filter: blur(40px); opacity: 0.3; border-radius: 50%; }
        
        .ss-hero-text h3 { font-size: 2.2rem; font-weight: 900; margin-bottom: 15px; }
        .ss-hero-text p { font-size: 1.15rem; color: #94a3b8; line-height: 1.7; max-width: 500px; margin-bottom: 30px; }
        .ss-hero-info { display: flex; gap: 15px; margin-bottom: 40px; }
        .ss-info-tag { background: #fff; padding: 8px 18px; border-radius: 100px; font-weight: 800; font-size: 0.9rem; display: flex; align-items: center; gap: 10px; color: #475569; border: 1px solid #e2e8f0; }
        
        .ss-hero-actions { display: flex; gap: 20px; }
        .ss-btn-primary { background: #6366f1; color: #fff; border: none; padding: 14px 35px; border-radius: 16px; font-weight: 900; font-family: inherit; font-size: 1.05rem; cursor: pointer; transition: 0.3s; display: flex; align-items: center; gap: 12px; }
        .ss-btn-primary:hover { background: #4f46e5; transform: translateY(-3px); box-shadow: 0 15px 30px rgba(99,102,241,0.25); }
        .ss-btn-outline { background: transparent; color: #0f172a; border: 1px solid #e2e8f0; padding: 14px 35px; border-radius: 16px; font-weight: 800; font-family: inherit; cursor: pointer; transition: 0.3s; display: flex; align-items: center; gap: 12px; }
        .ss-btn-outline:hover { background: #f8fafc; border-color: #0f172a; }
        .ss-btn-outline.active { background: #fff; color: #0f172a; }

        /* History */
        .ss-history-section { background: #f8fafc; border-radius: 40px; padding: 50px; border: 1px solid #f1f5f9; }
        .ss-section-title { display: flex; align-items: center; gap: 15px; margin-bottom: 35px; color: #0f172a; font-weight: 900; font-size: 1.4rem; }
        .ss-section-title i { color: #6366f1; }
        .ss-history-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px; }
        .ss-history-card { background: #fff; border-radius: 24px; padding: 25px; border: 1px solid #f1f5f9; position: relative; transition: 0.3s; }
        .ss-history-card:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.05); }
        .ss-h-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
        .ss-h-title h5 { font-size: 1.1rem; font-weight: 900; color: #1e293b; margin: 0 0 5px; }
        .ss-h-date { font-size: 0.8rem; color: #94a3b8; font-weight: 700; display: flex; align-items: center; gap: 5px; }
        .ss-h-del { color: #cbd5e1; background: transparent; border: none; font-size: 1.1rem; cursor: pointer; transition: 0.2s; }
        .ss-h-del:hover { color: #f43f5e; }
        .ss-h-stats { display: flex; gap: 15px; background: #f8fafc; padding: 15px; border-radius: 16px; margin-bottom: 20px; }
        .ss-h-stat { flex: 1; display: flex; flex-direction: column; gap: 4px; }
        .ss-h-stat .val { font-weight: 900; font-size: 1.1rem; color: #0f172a; }
        .ss-h-stat .lab { font-size: 0.75rem; color: #64748b; font-weight: 800; }
        .ss-h-badge { display: inline-block; padding: 6px 14px; border-radius: 10px; font-size: 0.8rem; font-weight: 900; }
        .ss-h-badge.pass { background: #ecfdf5; color: #10b981; }
        .ss-h-badge.fail { background: #fff1f2; color: #f43f5e; }

        /* Chat */
        .ss-chat-view { display: flex; justify-content: center; }
        .ss-chat-container { background: #fff; border: 1px solid #f1f5f9; border-radius: 35px; width: 100%; max-width: 650px; overflow: hidden; box-shadow: 0 30px 60px rgba(0,0,0,0.08); }
        .ss-chat-header { background: #6366f1; color: #fff; padding: 25px 35px; display: flex; align-items: center; gap: 15px; }
        .ss-chat-header img { width: 45px; height: 45px; border-radius: 50%; background: rgba(255,255,255,0.1); padding: 5px; }
        .ss-chat-status h5 { font-size: 1.1rem; font-weight: 900; margin: 0; }
        .ss-chat-status span { font-size: 0.8rem; color: #94a3b8; display: flex; align-items: center; gap: 6px; margin-top: 2px; }
        .ss-chat-status .dot { width: 6px; height: 6px; background: #10b981; border-radius: 50%; }
        .ss-chat-body { padding: 40px; background: #fdfdfd; height: 350px; overflow-y: auto; display: flex; flex-direction: column; gap: 20px; }
        .ss-msg.bot { align-self: flex-start; }
        .ss-msg.user { align-self: flex-end; }
        .ss-msg-bubble { padding: 16px 22px; border-radius: 20px; font-size: 1.05rem; font-weight: 800; line-height: 1.6; max-width: 85%; }
        .ss-msg.bot .ss-msg-bubble { background: #f1f5f9; color: #1e293b; border-bottom-right-radius: 4px; }
        .ss-chat-footer { padding: 35px; background: #fff; border-top: 1px solid #f1f5f9; }
        .ss-options-box { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 35px; }
        .ss-option-group label { display: block; font-size: 0.9rem; font-weight: 900; color: #0f172a; margin-bottom: 12px; }
        .ss-pills { display: flex; gap: 10px; flex-wrap: wrap; }
        .ss-pill { background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 18px; border-radius: 12px; font-family: inherit; font-weight: 800; font-size: 0.85rem; cursor: pointer; transition: 0.2s; }
        .ss-pill:hover { border-color: #cbd5e1; }
        .ss-pill.active { background: #6366f1; color: #fff; border-color: #6366f1; }
        .ss-generate-btn { width: 100%; background: #6366f1; color: #fff; border: none; padding: 18px; border-radius: 20px; font-weight: 900; font-family: inherit; font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 15px; transition: 0.3s; }
        .ss-generate-btn:hover { background: #4f46e5; transform: scale(1.02); }

        /* Loading */
        .ss-loading-view { text-align: center; padding: 100px 0; }
        .ss-ai-loader { max-width: 400px; margin: 0 auto; }
        .ss-loader-bot { position: relative; display: inline-block; margin-bottom: 40px; }
        .ss-loader-bot img { width: 120px; animation: ss-bounce 2s ease-in-out infinite; }
        .ss-loader-ring { position: absolute; inset: -15px; border: 4px solid #6366f1; border-radius: 50%; border-top-color: transparent; animation: ss-spin 1.5s linear infinite; }
        @keyframes ss-bounce { 0%,100%{ transform: translateY(0); } 50%{ transform: translateY(-15px); } }
        @keyframes ss-spin { to { transform: rotate(360deg); } }
        .ss-ai-loader h3 { font-size: 1.8rem; font-weight: 900; color: #0f172a; margin-bottom: 10px; }
        .ss-ai-loader p { color: #64748b; margin-bottom: 35px; }
        .ss-progress-track { height: 8px; background: #f1f5f9; border-radius: 100px; overflow: hidden; }
        .ss-progress-fill { height: 100%; background: #6366f1; border-radius: 100px; }

        /* Exam */
        .ss-exam-header-sticky { background: #fff; border: 1px solid #f1f5f9; border-radius: 28px; padding: 25px 35px; margin-bottom: 40px; position: sticky; top: 120px; z-index: 10; box-shadow: 0 10px 30px rgba(0,0,0,0.03); }
        .ss-header-top { display: flex; justify-content: space-between; align-items: center; }
        .ss-exam-title-box h4 { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin: 0 0 8px; }
        .ss-meta-pills { display: flex; gap: 10px; }
        .ss-meta-pill { font-size: 0.8rem; color: #64748b; font-weight: 800; background: #f8fafc; padding: 4px 12px; border-radius: 8px; display: flex; align-items: center; gap: 8px; }
        .ss-progress-summary { text-align: left; min-width: 150px; }
        .ss-progress-summary span { font-size: 0.85rem; font-weight: 900; color: #0f172a; margin-bottom: 8px; display: block; }
        .ss-mini-bar { height: 6px; background: #f1f5f9; border-radius: 100px; overflow: hidden; }
        .ss-mini-bar .fill { height: 100%; background: #6366f1; transition: 0.5s; }

        .ss-question-card { background: #fff; border: 1px solid #f1f5f9; border-radius: 35px; padding: 50px; margin-bottom: 40px; }
        .ss-question-num { color: #6366f1; font-weight: 900; font-size: 0.95rem; margin-bottom: 15px; }
        .ss-question-text { font-size: 1.6rem; font-weight: 900; color: #0f172a; line-height: 1.5; margin-bottom: 40px; }
        .ss-options-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .ss-option-btn { background: #f8fafc; border: 1px solid #f1f5f9; padding: 25px 30px; border-radius: 24px; text-align: right; font-family: inherit; font-weight: 800; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 15px; }
        .ss-option-btn:hover { border-color: #6366f1; background: #fff; }
        .ss-option-btn.selected { background: #fff; border: 2px solid #6366f1; box-shadow: 0 10px 25px rgba(232,146,10,0.1); }
        .ss-opt-label { width: 34px; height: 34px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 900; color: #64748b; flex-shrink: 0; }
        .ss-option-btn.selected .ss-opt-label { background: #6366f1; color: #fff; border-color: #6366f1; }
        .ss-opt-text { font-size: 1.1rem; color: #1e293b; flex: 1; }
        .ss-check { color: #6366f1; font-size: 1.2rem; }

        .ss-exam-footer { display: flex; justify-content: space-between; }
        .ss-footer-btn { padding: 16px 40px; border-radius: 20px; font-family: inherit; font-weight: 900; font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; gap: 12px; transition: 0.3s; border: none; }
        .ss-footer-btn.prev { background: #f8fafc; color: #64748b; }
        .ss-footer-btn.next { background: #6366f1; color: #fff; }
        .ss-footer-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ss-footer-btn:not(:disabled):hover { transform: scale(1.02); }

        /* Result */
        .ss-result-card { background: #fff; border: 1px solid #f1f5f9; border-radius: 40px; overflow: hidden; }
        .ss-result-header { text-align: center; padding: 60px 40px; color: #fff; }
        .ss-result-header.pass { background: #10b981; }
        .ss-result-header.fail { background: #f43f5e; }
        .ss-trophy-icon { font-size: 5rem; margin-bottom: 25px; opacity: 0.9; }
        .ss-result-header h2 { font-size: 2.8rem; font-weight: 900; margin-bottom: 10px; }
        .ss-result-header p { font-size: 1.2rem; opacity: 0.9; font-weight: 800; }
        
        .ss-result-stats { display: flex; justify-content: center; gap: 40px; padding: 40px; border-bottom: 1px solid #f1f5f9; }
        .ss-res-stat { text-align: center; }
        .ss-res-stat .val { font-size: 2.2rem; font-weight: 900; color: #0f172a; display: block; }
        .ss-res-stat .lab { font-size: 0.95rem; color: #64748b; font-weight: 800; margin-top: 5px; display: block; }

        .ss-result-actions { padding: 40px; display: flex; justify-content: center; gap: 20px; }
        .ss-btn-save { background: #0f172a; color: #fff; border: none; padding: 16px 35px; border-radius: 16px; font-weight: 900; font-family: inherit; cursor: pointer; display: flex; align-items: center; gap: 12px; }
        .ss-btn-retry { background: #6366f1; color: #fff; border: none; padding: 16px 35px; border-radius: 16px; font-weight: 900; font-family: inherit; cursor: pointer; display: flex; align-items: center; gap: 12px; }

        .ss-review-section { padding: 40px; background: #f8fafc; }
        .ss-review-section h5 { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin-bottom: 30px; }
        .ss-review-list { display: flex; flex-direction: column; gap: 25px; }
        .ss-review-item { background: #fff; border-radius: 24px; padding: 30px; border-right: 6px solid #cbd5e1; }
        .ss-review-item.correct { border-right-color: #10b981; }
        .ss-review-item.wrong { border-right-color: #f43f5e; }
        .ss-rev-q { font-size: 1.15rem; font-weight: 900; color: #1e293b; margin-bottom: 20px; }
        .ss-rev-options { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
        .ss-rev-opt { padding: 14px 20px; border-radius: 12px; background: #f8fafc; font-size: 0.95rem; font-weight: 800; display: flex; justify-content: space-between; align-items: center; }
        .ss-rev-opt.right { background: #ecfdf5; color: #059669; border: 1px solid #10b981; }
        .ss-rev-opt.user-wrong { background: #fff1f2; color: #e11d48; border: 1px solid #f43f5e; }

        @media(max-width:1100px){
          .ss-history-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media(max-width:768px){
          .ss-root { padding: 60px 0 80px; }
          .ss-container { padding: 0 20px; }
          .ss-hero-card { padding: 40px; }
          .ss-hero-content { flex-direction: column; text-align: center; gap: 30px; }
          .ss-history-grid { grid-template-columns: 1fr; }
          .ss-options-box { grid-template-columns: 1fr; }
          .ss-options-grid { grid-template-columns: 1fr; }
          .ss-rev-options { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

const HistoryCard = ({ item, idx, onDelete }) => (
  <motion.div
    className="ss-history-card"
    initial="hidden"
    animate="visible"
    variants={fadeUp}
    custom={idx}
  >
    <div className="ss-h-top">
      <div className="ss-h-title">
        <h5>{item.exam_title}</h5>
        <div className="ss-h-date">
          <FontAwesomeIcon icon={faCalendarAlt} />
          {new Date(item.completed_at).toLocaleDateString('ar-EG')}
        </div>
      </div>
      <button className="ss-h-del" onClick={() => onDelete(item)} title="حذف">
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </div>
    <div className="ss-h-stats">
      <div className="ss-h-stat">
        <span className="val">{item.total_questions}</span>
        <span className="lab">الأسئلة</span>
      </div>
      <div className="ss-h-stat">
        <span className="val">{item.correct_answers}</span>
        <span className="lab">الصح</span>
      </div>
      <div className="ss-h-stat">
        <span className="val">{item.score_percentage}%</span>
        <span className="lab">النسبة</span>
      </div>
    </div>
    <div className={`ss-h-badge ${item.is_passed ? 'pass' : 'fail'}`}>
      {item.is_passed ? "ناجح" : "راسب"}
    </div>
  </motion.div>
);

export default StudentSelfExam;
